import { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Send, Upload } from 'lucide-react';
import {
  CALL_STATUS_OPTIONS,
  CALL_STATUS_TONE,
  CALL_STATUSES,
  NO_SURVEY_LABEL,
  PERMISSIONS,
  SURVEY_OUTCOME_OPTIONS,
  SURVEY_OUTCOME_TONE,
  SURVEY_OUTCOMES,
  TRACKER_FDFC_DISPATCH_MAX,
  trackerFdfcDispatchProblems,
  type SurveyOutcome,
  type TrackerFdfcDispatchStatus,
  type TrackerFdfcEntry,
} from '@tracker/shared';
import { CallStatusSelect, ToneLabel } from './CallStatusSelect';
import { SurveyOutcomeSelect } from './SurveyOutcomeSelect';
import { DispatchBadge } from './DispatchBadge';
import { DISPATCH_LABEL } from './dispatch-labels';
import { trackerFdfcService } from '@/services/tracker-fdfc.service';
import { formatDateTime } from '@/lib/dateFormat';
import { usePermission } from '@/hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { TrackerFdfcImportDialog } from './TrackerFdfcImportDialog';
import { TrackerFdfcDispatchDialog } from './TrackerFdfcDispatchDialog';
import { DataTable, type Column } from '@/components/ui/data-table';
import { SearchInput } from '@/components/ui/search-input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// New webhook calls land without anyone touching the page; poll so the list keeps up.
const REFRESH_MS = 30_000;

function cityLine(r: Pick<TrackerFdfcEntry, 'city' | 'state' | 'pincode'>): string {
  return [r.city, r.state, r.pincode].filter(Boolean).join(', ') || '—';
}

const DISPATCH_STATUSES = Object.keys(DISPATCH_LABEL) as TrackerFdfcDispatchStatus[];

/** A URL value if it is one of the allowed ones, else '' (a hand-edited URL must not break the list). */
function pick<T extends string>(value: string | null, allowed: readonly T[]): T | '' {
  return value && (allowed as readonly string[]).includes(value) ? (value as T) : '';
}

export function TrackerFdfcPage() {
  // Page, search and filters live in the URL, so opening an entry and coming
  // back (link or browser Back) lands on the same view.
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const page = Math.max(1, Number(params.get('page')) || 1);
  const search = params.get('q') ?? '';
  const courseLevel = params.get('level') ?? '';
  const dispatchStatus = pick(params.get('dispatch'), DISPATCH_STATUSES);
  const callStatus = pick(params.get('call'), CALL_STATUSES);
  const surveyOutcome = pick<SurveyOutcome | 'none'>(params.get('survey'), [...SURVEY_OUTCOMES, 'none']);

  /** Sets one filter (empty removes it) and goes back to page 1. */
  const setFilter = (key: string, value: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  const setPage = (p: number) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (p > 1) next.set('page', String(p));
      else next.delete('page');
      return next;
    });

  const [importOpen, setImportOpen] = useState(false);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  // Kept as whole rows (not ids) so the selection survives paging and the
  // dispatch dialog can show who is in it.
  const [selected, setSelected] = useState<Map<string, TrackerFdfcEntry>>(new Map());
  const perms = usePermission();
  const canDispatch = perms.has(PERMISSIONS.TRACKER_FDFC_DISPATCH);
  const canUpdate = perms.has(PERMISSIONS.TRACKER_FDFC_UPDATE);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [
      'tracker-fdfc',
      {
        page,
        q: search,
        course_level: courseLevel,
        dispatch_status: dispatchStatus,
        call_status: callStatus,
        survey_outcome: surveyOutcome,
      },
    ],
    queryFn: () =>
      trackerFdfcService.list({
        page,
        limit: 20,
        q: search || undefined,
        course_level: courseLevel || undefined,
        dispatch_status: dispatchStatus || undefined,
        call_status: callStatus || undefined,
        survey_outcome: surveyOutcome || undefined,
      }),
    refetchInterval: REFRESH_MS,
  });

  const { data: levels } = useQuery({
    queryKey: ['tracker-fdfc', 'course-levels'],
    queryFn: () => trackerFdfcService.courseLevels(),
  });

  const rows = data?.data ?? [];
  // Only rows that have not gone (or may have gone) to NSDC can be ticked.
  const selectable = (r: TrackerFdfcEntry) => ['not_dispatched', 'failed'].includes(r.dispatch_status);
  const pageSelectable = rows.filter(selectable);
  const allOnPage = pageSelectable.length > 0 && pageSelectable.every((r) => selected.has(r.id));
  const someOnPage = !allOnPage && pageSelectable.some((r) => selected.has(r.id));

  const toggle = (r: TrackerFdfcEntry, on: boolean) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (on) {
        if (next.size >= TRACKER_FDFC_DISPATCH_MAX) {
          toast.error(`Select ${TRACKER_FDFC_DISPATCH_MAX} entries or fewer per dispatch.`);
          return prev;
        }
        next.set(r.id, r);
      } else next.delete(r.id);
      return next;
    });
  };

  const togglePage = (on: boolean) => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const r of pageSelectable) {
        if (!on) next.delete(r.id);
        else if (next.size < TRACKER_FDFC_DISPATCH_MAX) next.set(r.id, r);
      }
      return next;
    });
  };

  const selectColumn: Column<TrackerFdfcEntry> = {
    key: 'select',
    header: (
      <Checkbox
        aria-label="Select all sendable rows on this page"
        // Sits on the coloured header row: a light outline keeps it visible there.
        className="border-white/80 checked:border-white indeterminate:border-white"
        checked={allOnPage}
        indeterminate={someOnPage}
        disabled={pageSelectable.length === 0}
        onChange={(e) => togglePage(someOnPage ? true : e.target.checked)}
      />
    ),
    className: 'w-10',
    cell: (r) =>
      selectable(r) ? (
        <Checkbox
          aria-label={`Select ${r.full_name ?? r.mobile_number}`}
          checked={selected.has(r.id)}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => toggle(r, e.target.checked)}
        />
      ) : null,
  };

  const columns: Column<TrackerFdfcEntry>[] = [
    ...(canDispatch ? [selectColumn] : []),
    {
      key: 'full_name',
      header: 'Name',
      cell: (r) => <span className="font-medium">{r.full_name ?? '—'}</span>,
    },
    {
      key: 'mobile_number',
      header: 'Mobile',
      cell: (r) => <span className="font-mono text-xs">{r.mobile_number}</span>,
    },
    {
      key: 'course_level',
      header: 'Course Level',
      cell: (r) => (r.course_level ? <Badge>{r.course_level}</Badge> : '—'),
    },
    {
      key: 'city',
      header: 'City / State / Pincode',
      cell: (r) => <span className="text-sm text-muted-foreground">{cityLine(r)}</span>,
    },
    {
      key: 'receive_count',
      header: 'Received',
      className: 'text-center',
      cell: (r) => (
        <Badge variant={r.receive_count > 1 ? 'warning' : 'secondary'}>{r.receive_count}×</Badge>
      ),
    },
    {
      key: 'dispatch_status',
      header: 'NSDC Dispatch',
      cell: (r) => <DispatchBadge entry={r} />,
    },
    {
      key: 'survey_outcome',
      header: 'Survey',
      cell: (r) => (
        <SurveyOutcomeSelect
          id={r.id}
          outcome={r.survey_outcome}
          damageAreas={r.survey_damage_areas}
          damageDetail={r.survey_damage_detail}
          damageClaim={r.survey_damage_claim}
          disabled={!canUpdate}
        />
      ),
    },
    {
      key: 'call_status',
      header: 'Call',
      cell: (r) => <CallStatusSelect id={r.id} status={r.call_status} disabled={!canUpdate} />,
    },
    {
      key: 'last_received_at',
      header: 'Last Received',
      cell: (r) => <span className="whitespace-nowrap text-xs">{formatDateTime(r.last_received_at)}</span>,
    },
  ];

  // The newest copy of each ticked row: an address fixed after ticking must be
  // judged (and sent) as it is now, not as it was when the box was ticked.
  const freshById = new Map(rows.map((r) => [r.id, r]));
  const selectedRows = [...selected.values()].map((r) => freshById.get(r.id) ?? r);
  const selectedReady = selectedRows.filter((r) => trackerFdfcDispatchProblems(r).length === 0).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">FDFC Tracker</h1>
          <p className="text-sm text-muted-foreground">
            Entries received by the FDFC Tracker webhook or imported from Excel. One row per
            mobile number; a repeat submission updates the same row.
            {data ? ` ${data.pagination.total} total.` : ''}
          </p>
        </div>
        {perms.has(PERMISSIONS.TRACKER_FDFC_IMPORT) && (
          <Button className="shrink-0" onClick={() => setImportOpen(true)}>
            <Upload className="h-4 w-4" /> Import Data
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <SearchInput
          value={search}
          onChange={(v) => setFilter('q', v)}
          placeholder="Search name, mobile, city, pincode…"
          containerClassName="w-full sm:max-w-sm"
        />
        <Select
          value={courseLevel || '__all__'}
          onValueChange={(v) => setFilter('level', v === '__all__' ? '' : v)}
        >
          <SelectTrigger className="h-9 w-full sm:w-44">
            <SelectValue placeholder="Course Level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Course Levels</SelectItem>
            {(levels ?? []).map((l) => (
              <SelectItem key={l} value={l}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={dispatchStatus || '__all__'}
          onValueChange={(v) => setFilter('dispatch', v === '__all__' ? '' : v)}
        >
          <SelectTrigger className="h-9 w-full sm:w-44">
            <SelectValue placeholder="NSDC Dispatch" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Dispatch</SelectItem>
            {DISPATCH_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>{DISPATCH_LABEL[s]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={surveyOutcome || '__all__'}
          onValueChange={(v) => setFilter('survey', v === '__all__' ? '' : v)}
        >
          <SelectTrigger className="h-9 w-full sm:w-44">
            <SelectValue placeholder="Survey" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Surveys</SelectItem>
            <SelectItem value="none">
              <ToneLabel tone="neutral">{NO_SURVEY_LABEL}</ToneLabel>
            </SelectItem>
            {SURVEY_OUTCOME_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                <ToneLabel tone={SURVEY_OUTCOME_TONE[o.value]}>{o.label}</ToneLabel>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={callStatus || '__all__'}
          onValueChange={(v) => setFilter('call', v === '__all__' ? '' : v)}
        >
          <SelectTrigger className="h-9 w-full sm:w-44">
            <SelectValue placeholder="Call" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Calls</SelectItem>
            {CALL_STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                <ToneLabel tone={CALL_STATUS_TONE[o.value]}>{o.label}</ToneLabel>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {canDispatch && selected.size > 0 && (
        <div className="sticky top-2 z-10 flex flex-wrap items-center gap-3 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-sm backdrop-blur">
          <span className="font-medium">
            {selected.size} selected
            {selectedReady < selected.size && (
              <span className="text-muted-foreground"> · {selectedReady} ready</span>
            )}
          </span>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setSelected(new Map())}
          >
            Clear
          </button>
          <Button size="sm" className="ml-auto" onClick={() => setDispatchOpen(true)}>
            <Send className="h-4 w-4" /> Dispatch {selected.size}
          </Button>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/tracker-fdfc/${r.id}`, { state: { search: location.search } })}
        loading={isLoading}
        error={isError}
        onRetry={() => void refetch()}
        empty={
          search || courseLevel || dispatchStatus || callStatus || surveyOutcome
            ? 'No entries match the filters'
            : 'No entries received yet'
        }
        pagination={{
          page,
          totalPages: data?.pagination.totalPages ?? 1,
          onPageChange: setPage,
        }}
      />

      <TrackerFdfcImportDialog open={importOpen} onClose={() => setImportOpen(false)} />
      <TrackerFdfcDispatchDialog
        open={dispatchOpen}
        entries={selectedRows}
        onClose={() => setDispatchOpen(false)}
        onSent={() => setSelected(new Map())}
      />
    </div>
  );
}
